import React, { useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, FileSpreadsheet, AlertTriangle, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { parse as parseCSV } from "@/lib/csv";

// columnDefs shape: [{ key, label, type, options, userList, isCustom, colId, parseText, setValInto }]
// context: { userMap } — same as SheetKit context
// canonical row-shape for backend /tasks/bulk-import:
//   { keyword, date, niche, ai_score, draft, method, writer_id, publisher_id, status, check, notes, docs, custom }

const BASE_KEY_FIELDS = new Set([
  "date", "keyword", "niche", "ai_score", "draft", "method",
  "writer_id", "publisher_id", "status", "check", "notes", "docs",
]);

export default function ImportCSVDialog({ open, onClose, columnDefs, context, onImport }) {
  const fileInputRef = useRef(null);
  const [rawText, setRawText] = useState("");
  const [parsed, setParsed] = useState(null); // { headers, rows }
  const [mapping, setMapping] = useState({});   // headerIndex -> columnDef.key (or "" to skip)
  const [position, setPosition] = useState("bottom");
  const [submitting, setSubmitting] = useState(false);

  const importableCols = useMemo(
    () => columnDefs.filter((c) => c.editable !== false && c.parseText),
    [columnDefs]
  );

  const guessMap = (headers) => {
    const m = {};
    const lc = (s) => (s || "").trim().toLowerCase();
    headers.forEach((h, i) => {
      const key = lc(h);
      const hit = importableCols.find((c) => lc(c.label) === key || lc(c.key) === key);
      m[i] = hit ? hit.key : "";
    });
    return m;
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("File too large (max 5 MB)"); return; }
    const text = await file.text();
    setRawText(text);
    parseAndSet(text);
  };
  const parseAndSet = (text) => {
    if (!text.trim()) { setParsed(null); return; }
    const rows = parseCSV(text);
    if (rows.length < 2) { toast.error("CSV must have a header row and at least one data row"); return; }
    const [headers, ...data] = rows;
    setParsed({ headers, rows: data });
    setMapping(guessMap(headers));
  };

  const previewRows = useMemo(() => (parsed?.rows || []).slice(0, 5), [parsed]);
  const totalRows = parsed?.rows?.length || 0;

  const keywordColIdx = useMemo(() => {
    if (!parsed) return -1;
    const entry = Object.entries(mapping).find(([, k]) => k === "keyword");
    return entry ? Number(entry[0]) : -1;
  }, [mapping, parsed]);

  const canSubmit = parsed && keywordColIdx >= 0 && totalRows > 0 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const bodyRows = [];
      const skipped = [];
      for (let ri = 0; ri < parsed.rows.length; ri++) {
        const raw = parsed.rows[ri];
        const rowObj = { custom: {} };
        for (const [hi, colKey] of Object.entries(mapping)) {
          if (!colKey) continue;
          const col = importableCols.find((c) => c.key === colKey);
          if (!col) continue;
          const cellText = (raw[Number(hi)] ?? "").trim();
          if (cellText === "") continue;
          let val;
          try { val = col.parseText(cellText, context); } catch { val = undefined; }
          if (val === undefined) continue; // unparseable — silently skip cell
          if (col.isCustom) rowObj.custom[col.colId] = val;
          else if (BASE_KEY_FIELDS.has(col.key)) rowObj[col.key] = val;
        }
        if (!rowObj.keyword || String(rowObj.keyword).trim() === "") { skipped.push(ri); continue; }
        if (Object.keys(rowObj.custom).length === 0) delete rowObj.custom;
        bodyRows.push(rowObj);
      }
      if (bodyRows.length === 0) { toast.error("No rows had a valid Keyword"); setSubmitting(false); return; }
      await onImport({ rows: bodyRows, position });
      const skipMsg = skipped.length > 0 ? ` (skipped ${skipped.length} row${skipped.length === 1 ? "" : "s"} without Keyword)` : "";
      toast.success(`Imported ${bodyRows.length} task${bodyRows.length === 1 ? "" : "s"}${skipMsg}`);
      reset();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Import failed");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setRawText(""); setParsed(null); setMapping({}); setPosition("bottom");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { reset(); onClose(); } }}>
      <DialogContent className="max-w-3xl" data-testid="import-csv-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="h-5 w-5 text-purple-600" /> Import tasks from CSV</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step 1 — file / paste */}
          {!parsed && (
            <div className="space-y-3">
              <div
                className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-purple-400 hover:bg-purple-50/30 cursor-pointer transition"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); }}
                onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
                data-testid="csv-drop-zone"
              >
                <Upload className="h-8 w-8 mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-700 font-medium">Click to choose a CSV file or drop it here</p>
                <p className="text-xs text-slate-500 mt-1">First row must be headers. Max 5 MB / 2000 rows.</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="csv-file-input"
                />
              </div>
              <div className="text-xs text-slate-500 text-center">— or —</div>
              <div>
                <Label className="text-xs">Paste CSV text</Label>
                <textarea
                  className="w-full mt-1 border border-slate-300 rounded-md p-2 text-sm font-mono h-32 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Keyword,Niche,Status&#10;best crm,SaaS,To-Do"
                  value={rawText}
                  onChange={(e) => setRawText(e.target.value)}
                  data-testid="csv-paste-textarea"
                />
                <div className="mt-2 flex justify-end">
                  <Button variant="outline" size="sm" onClick={() => parseAndSet(rawText)} disabled={!rawText.trim()} data-testid="csv-parse-btn">Parse pasted CSV</Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2 — mapping + preview */}
          {parsed && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span className="font-medium">{totalRows} row{totalRows === 1 ? "" : "s"} parsed</span>
                  <span className="text-slate-500">· {parsed.headers.length} column{parsed.headers.length === 1 ? "" : "s"}</span>
                </div>
                <Button variant="ghost" size="sm" onClick={reset} data-testid="csv-reset-btn"><X className="h-4 w-4 mr-1" /> Choose another file</Button>
              </div>

              {keywordColIdx < 0 && (
                <div className="flex items-start gap-2 p-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  <span>Please map at least one column to <strong>Keyword</strong> — rows without a keyword will be skipped.</span>
                </div>
              )}

              {/* Column mapping */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600 flex justify-between">
                  <span>Column mapping</span>
                  <span className="text-slate-400">CSV header → Task column</span>
                </div>
                <div className="max-h-64 overflow-auto divide-y divide-slate-100">
                  {parsed.headers.map((h, i) => (
                    <div key={i} className="flex items-center gap-3 px-3 py-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate">{h || <em className="text-slate-400">(empty)</em>}</div>
                        <div className="text-xs text-slate-500 truncate">e.g. {previewRows[0]?.[i] || <em>—</em>}</div>
                      </div>
                      <span className="text-slate-400">→</span>
                      <div className="w-56">
                        <Select value={mapping[i] || "__skip__"} onValueChange={(v) => setMapping((m) => ({ ...m, [i]: v === "__skip__" ? "" : v }))}>
                          <SelectTrigger className="h-8 text-xs" data-testid={`csv-map-${i}`}>
                            <SelectValue placeholder="Skip" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__skip__">— Skip —</SelectItem>
                            {importableCols.map((c) => (
                              <SelectItem key={c.key} value={c.key}>{c.label}{c.key === "keyword" ? " *" : ""}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">Preview (first 5 rows)</div>
                <div className="overflow-auto max-h-48">
                  <table className="text-xs w-full">
                    <thead className="bg-white sticky top-0">
                      <tr>{parsed.headers.map((h, i) => <th key={i} className="text-left px-3 py-1.5 font-medium text-slate-500 border-b border-slate-200">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {previewRows.map((r, ri) => (
                        <tr key={ri} className="border-b border-slate-100">
                          {parsed.headers.map((_, ci) => <td key={ci} className="px-3 py-1.5 text-slate-700 max-w-[180px] truncate">{r[ci] || ""}</td>)}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Position */}
              <div>
                <Label className="text-xs">Insert position</Label>
                <RadioGroup value={position} onValueChange={setPosition} className="flex gap-4 mt-1">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <RadioGroupItem value="top" data-testid="csv-pos-top" /> Top of task list
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <RadioGroupItem value="bottom" data-testid="csv-pos-bottom" /> Bottom of task list
                  </label>
                </RadioGroup>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }} data-testid="csv-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit} className="bg-purple-600 hover:bg-purple-700" data-testid="csv-import-submit-btn">
            {submitting ? "Importing…" : parsed ? `Import ${totalRows} row${totalRows === 1 ? "" : "s"}` : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
